import { useEffect } from 'react'
import { useForm, Controller, useFieldArray } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, TextField,
  Alert, CircularProgress, IconButton, Autocomplete,
} from '@mui/material'
import { Add, DeleteOutlined } from '@mui/icons-material'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { saveQuantityDiscountTiers, type ArticleQuantityDiscounts } from '../../api/preturi'
import { listArticles } from '../../api/nomenclatoare'
import { errorMessage } from '../../api/client'

const tierSchema = z.object({
  id: z.string().nullable(),
  minQuantity: z.string().min(1, 'Câmp obligatoriu'),
  discountPercent: z.string().min(1, 'Câmp obligatoriu'),
})
const schema = z.object({
  articleId: z.string().min(1, 'Selectați un articol'),
  tiers: z.array(tierSchema).min(1, 'Adăugați cel puțin un prag'),
})
type FormValues = z.infer<typeof schema>

const EMPTY: FormValues = { articleId: '', tiers: [] }

export default function QuantityTierFormDialog({ target, onClose }: { target: ArticleQuantityDiscounts | null | 'new'; onClose: () => void }) {
  const open = target !== null
  const editing = target !== null && target !== 'new'
  const qc = useQueryClient()

  const { data: articles } = useQuery({ queryKey: ['nomenclatoare', 'articole', 'toate'], queryFn: () => listArticles(), enabled: open })

  const { control, handleSubmit, reset, formState: { errors } } = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: EMPTY })
  const tiers = useFieldArray({ control, name: 'tiers' })

  useEffect(() => {
    if (editing) {
      const d = target as ArticleQuantityDiscounts
      reset({ articleId: d.articleId, tiers: d.tiers.map((t) => ({ id: t.id, minQuantity: String(t.minQuantity), discountPercent: String(t.discountPercent) })) })
    } else if (target === 'new') {
      reset(EMPTY)
    }
  }, [target, editing, reset])

  const mutation = useMutation({
    mutationFn: async (values: FormValues) => saveQuantityDiscountTiers(
      values.articleId,
      values.tiers.map((t) => ({ id: t.id, minQuantity: Number(t.minQuantity), discountPercent: Number(t.discountPercent) })),
    ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['comercial', 'preturi', 'praguri-cantitate'] })
      onClose()
    },
  })

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit((v) => mutation.mutate(v))}>
        <DialogTitle>{editing ? 'Editare praguri de discount' : 'Praguri de discount noi'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <Controller name="articleId" control={control} render={({ field }) => (
              <Autocomplete
                disabled={editing} options={articles?.map((a) => a.id) ?? []}
                getOptionLabel={(id) => { const a = articles?.find((x) => x.id === id); return a ? `${a.code} — ${a.shortDescription}` : id }}
                value={field.value || null} onChange={(_, v) => field.onChange(v ?? '')}
                renderInput={(params) => <TextField {...params} label="Articol" error={!!errors.articleId} helperText={errors.articleId?.message} />}
              />
            )} />

            {typeof errors.tiers?.message === 'string' && <Alert severity="warning">{errors.tiers.message}</Alert>}
            {tiers.fields.map((f, index) => (
              <Stack key={f.id} direction="row" spacing={1.5} sx={{ alignItems: 'flex-start' }}>
                <Controller name={`tiers.${index}.minQuantity`} control={control} render={({ field }) => (
                  <TextField {...field} type="number" label="Cantitate minimă" fullWidth
                    error={!!errors.tiers?.[index]?.minQuantity} helperText={errors.tiers?.[index]?.minQuantity?.message} />
                )} />
                <Controller name={`tiers.${index}.discountPercent`} control={control} render={({ field }) => (
                  <TextField {...field} type="number" label="Discount (%)" fullWidth
                    error={!!errors.tiers?.[index]?.discountPercent} helperText={errors.tiers?.[index]?.discountPercent?.message} />
                )} />
                <IconButton onClick={() => tiers.remove(index)} sx={{ mt: 0.5 }}><DeleteOutlined fontSize="small" /></IconButton>
              </Stack>
            ))}
            <Button startIcon={<Add />} onClick={() => tiers.append({ id: null, minQuantity: '', discountPercent: '' })} sx={{ alignSelf: 'flex-start' }}>
              Adaugă prag
            </Button>

            {Object.keys(errors).length > 0 && (
              <Alert severity="warning">Există câmpuri obligatorii necompletate — verificați câmpurile marcate cu roșu mai sus.</Alert>
            )}
            {mutation.isError && <Alert severity="error">{errorMessage(mutation.error)}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={onClose}>Renunță</Button>
          <Button type="submit" variant="contained" disabled={mutation.isPending}
            startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}>
            Salvează
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
